/**
 *  @class
 *  @function ProductRecommendations
 */
class ProductRecommendations extends HTMLElement {
	constructor() {
		super();
		this.parent = this.closest('.product-recommendations--parent');
		this.splide = null;
		this.isInitialized = false;
	}

	fetchProducts() {
		fetch(this.dataset.url)
			.then(response => response.text())
			.then(text => {
				const html = document.createElement('div');
				html.innerHTML = text;
				const recommendations = html.querySelector('product-recommendations');

				if (recommendations && recommendations.innerHTML.trim().length) {
					this.innerHTML = recommendations.innerHTML;

					if (this.parent) {
						this.parent.classList.add('product-recommendations--full');

						if (document.body.classList.contains('open-cart')) {
							this.parent.classList.add('active');
						}
					}
					
					// Initialize Splide after content is loaded
					this.initializeSplide();
				}

				this.classList.add('product-recommendations--loaded');
			})
			.catch(e => {
				console.error(e);
			});
	}

	initializeSplide() {
		if (this.isInitialized) return;

		const splideElement = this.querySelector('.splide');
		if (!splideElement) return;

		this.loadSplideAssets().then(() => {
			this.createSplideInstance(splideElement);
		});
	}

	loadSplideAssets() {
		return new Promise((resolve) => {
			if (window.Splide) {
				resolve();
				return;
			}

			// Load CSS if not already loaded
			if (!document.querySelector('link[href*="splide"]')) {
				const cssLink = document.createElement('link');
				cssLink.rel = 'stylesheet';
				cssLink.href = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css';
				document.head.appendChild(cssLink);
			}

			// Load JS
			const script = document.createElement('script');
			script.src = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js';
			script.onload = resolve;
			document.head.appendChild(script);
		});
	}

	createSplideInstance(splideElement) {
		const isMobile = window.innerWidth < 768;
		const perPage = isMobile ? 2 : 4;

		const splideOptions = {
			type: 'slide',
			perPage: perPage,
			perMove: 1,
			gap: '1.5rem',
			arrows: false,
			drag: true,
			snap: true,
			keyboard: true,
			focus: 0,
			trimSpace: true,
			autoWidth: false,
			rewind: false,
			clones: 0,
			pagination: false,
			omitEnd: false,
			breakpoints: {
				767: {
					perPage: 2,
					gap: '6px',
					arrows: false,
				},
				1024: {
					perPage: 2,
					gap: '16px',
					arrows: false,
				}
			},
			classes: {
				prev: 'splide__arrow--prev slider-nav slider-nav--prev',
				next: 'splide__arrow--next slider-nav slider-nav--next',
			}
		};

		this.splide = new Splide(splideElement, splideOptions);
		
		// Setup features
		this.setupProgressBar();
		this.setupOverflowHandler();
		
		// Mount and initialize
		this.splide.mount();
		this.handleArrowStates();
		this.setupResizeHandler();

		this.isInitialized = true;
	}

	setupProgressBar() {
	const progressBar = this.querySelector('.slider-progress-bar');
	if (!progressBar) return;

	const updateProgress = () => {
		const isOverflow = this.splide.Components.Layout.isOverflow();
		const slidesCount = this.querySelectorAll('.main.splide__slide').length;
		const perPage = this.splide.options.perPage;

		if (!isOverflow) {
			progressBar.style.width = '100%';
			return;
		}

		const currentIndex = this.splide.index;
		const maxIndex = Math.max(0, slidesCount - perPage);

		// Calculate progress: shows how much content is visible
		// At start (index 0): shows perPage/slidesCount
		// At end (index maxIndex): shows 100%
		const visibleStart = currentIndex;
		const visibleEnd = currentIndex + perPage;
		const progress = (visibleEnd / slidesCount) * 100;
		
		progressBar.style.width = `${Math.min(progress, 100)}%`;
	};

	this.splide.on('moved', updateProgress);
	this.splide.on('overflow', updateProgress);
	this.splide.on('mounted', updateProgress);
}

	setupOverflowHandler() {
		this.splide.on('overflow', (isOverflow) => {
			const arrows = this.querySelectorAll('.splide__arrow');
			const track = this.querySelector('.splide__track');

			if (isOverflow) {
				arrows.forEach(arrow => {
					arrow.style.display = '';
					arrow.disabled = false;
				});
				if (track) {
					track.style.cursor = 'grab';
				}
			} else {
				// arrows.forEach(arrow => {
				// 	arrow.style.display = 'none';
				// });
				if (track) {
					track.style.cursor = 'default';
				}
			}
		});
	}

	setupResizeHandler() {
		let resizeTimeout;

		window.addEventListener('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				if (this.splide) {
					const isMobile = window.innerWidth < 768;
					const perPage = isMobile ? 2 : 4;

					this.splide.options = {
						...this.splide.options,
						perPage: perPage,
						arrows: !isMobile,
					};

					this.splide.go(0);
					this.splide.refresh();
					this.splide.emit('updated');
				}
			}, 250);
		});
	}

	handleArrowStates() {
		const prevArrow = this.querySelector('.splide__arrow--prev');
		const nextArrow = this.querySelector('.splide__arrow--next');

		const updateArrowState = () => {
			const isOverflow = this.splide.Components.Layout.isOverflow();
			const totalSlides = this.splide.Components.Slides.getLength();
			const perPage = this.splide.options.perPage;
			const index = this.splide.index;
			const maxIndex = Math.max(0, totalSlides - perPage);

			if (!isOverflow) {
				if (prevArrow) prevArrow.disabled = true;
				if (nextArrow) nextArrow.disabled = true;
				return;
			}

			if (prevArrow) prevArrow.disabled = index <= 0;
			if (nextArrow) nextArrow.disabled = index >= maxIndex;
		};

		this.splide.on('mounted moved updated overflow', updateArrowState);
		updateArrowState();
	}

	connectedCallback() {
		this.fetchProducts();
	}

	disconnectedCallback() {
		if (this.splide) {
			this.splide.destroy();
			this.splide = null;
		}
	}
}

customElements.define('product-recommendations', ProductRecommendations);